import { ProfileCard } from '@dealstack/frontend/shared/components/ProfileCard/ProfileCard';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Container, Flex, Text, Drawer } from '@mantine/core';
import { useState } from 'react';
import { getFullName } from '@dealstack/frontend/shared/components/UserSidebar/UserSidebar.utils';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface Props {
	firstName: string;
	lastName: string;
	email: string;
	deleteFromText: string;
	onRemoveUserClicked: () => void;
	showRemoveUserButton: boolean;
	showIsContactPerson: boolean;
	isContactPerson: boolean;
	disableIsContactPerson: boolean;
	onUpdateContactPersonClicked: (isContactPerson: boolean) => void;
}

export const UserSidebar = ({
	firstName,
	lastName,
	email,
	deleteFromText,
	showRemoveUserButton,
	onRemoveUserClicked,
	showIsContactPerson,
	isContactPerson,
	disableIsContactPerson,
	onUpdateContactPersonClicked,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const [showDrawer, setShowDrawer] = useState(false);
	const handleRemoveUserClicked = () => {
		onRemoveUserClicked();
		setShowDrawer(false);
	};

	const fullName = getFullName(firstName, lastName);

	return (
		<Container
			p="sm"
			sx={(theme) => ({
				border: `1px solid ${theme.colors.gray[2]}`,
				borderRadius: '0.5rem',
			})}
			bg="white.0"
		>
			<Flex gap="1rem" direction="column" align="center">
				<Flex gap="1rem" direction="column" align="flex-start">
					<ProfileCard name={fullName} email={email} />
					{showIsContactPerson && disableIsContactPerson && (
						<Flex gap="0.5rem" align="center">
							<Icon variant="warning" size="md" color="red" />
							<Text size="sm" c="red">
								{tCommonUi('user_sidebar.make_contact_person_disabled_reason')}
							</Text>
						</Flex>
					)}
					{showIsContactPerson && (
						<AdvancedSwitch
							rightText={tCommonUi('user_sidebar.make_contact_person')}
							selectedValue={isContactPerson}
							onChange={(event) => onUpdateContactPersonClicked(!!event)}
							disabled={disableIsContactPerson}
							dataTestId={TEST_ELEMENT_IDS.MIP_CONTACT_PERSON_SWITCH}
							selectedColor="dark.8"
							unselectedColor="dark.8"
						/>
					)}
				</Flex>
				{showRemoveUserButton && (
					<Button
						data-testid={TEST_ELEMENT_IDS.USER_DETAIL_REMOVE_BUTTON}
						color="red"
						variant="outline"
						sx={{ border: 'none' }}
						onClick={() => setShowDrawer(true)}
					>
						{tCommonUi('user_sidebar.remove_user_from', { deleteFromText })}
					</Button>
				)}
				<Drawer
					opened={showDrawer}
					onClose={() => setShowDrawer(false)}
					position="right"
					transitionProps={drawerTransitionProps}
					sx={() => ({
						'.mantine-Drawer-header': {
							alignItems: 'flex-start',
						},
					})}
					trapFocus={false}
					title={
						<Text>
							{tCommonUi('user_sidebar.remove_user_from', { deleteFromText })}
						</Text>
					}
				>
					<Flex direction="column" gap="1rem" justify="space-between" h="100%">
						<Text>
							{tCommonUi('user_sidebar.remove_confirmation', {
								name: `${firstName} ${lastName}`,
								deleteFromText,
							})}
						</Text>
						<Flex gap="1rem" direction="column">
							<Button onClick={handleRemoveUserClicked} color="red">
								{tCommonUi('common_buttons.remove')}
							</Button>
							<Button
								onClick={() => setShowDrawer(false)}
								variant="outline"
								sx={{
									border: 'none',
								}}
							>
								{tCommonUi('common_buttons.cancel')}
							</Button>
						</Flex>
					</Flex>
				</Drawer>
			</Flex>
		</Container>
	);
};
